(() => {
  if (window.collectionProductGridScriptLoaded) return;
  window.collectionProductGridScriptLoaded = true;

  let adCardHeightFrame;
  let observedAdCardGrid;
  const adCardGridResizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(() => scheduleAdCardHeightSync(observedAdCardGrid))
    : null;

  const getNaturalProductCardHeight = (gridItem) => {
    const productCard = gridItem?.querySelector('.new-product-card-2') || gridItem?.firstElementChild;
    if (!productCard) return 0;

    const inlineHeight = productCard.style.height;
    productCard.style.height = 'auto';
    const height = productCard.getBoundingClientRect().height;
    productCard.style.height = inlineHeight;
    return height;
  };

  const syncAdCardHeights = (grid) => {
    if (!grid?.isConnected) return;

    const items = Array.from(grid.children);
    const adCards = items.filter((item) => item.hasAttribute('data-collection-ad-card-mounted'));

    adCards.forEach((adCard) => {
      const cardIndex = items.indexOf(adCard);
      let referenceItem;

      for (let index = cardIndex - 1; index >= 0; index -= 1) {
        if (!items[index].hasAttribute('data-collection-ad-card-mounted')) {
          referenceItem = items[index];
          break;
        }
      }

      if (!referenceItem) {
        referenceItem = items.slice(cardIndex + 1).find((item) =>
          !item.hasAttribute('data-collection-ad-card-mounted')
        );
      }

      const referenceHeight = getNaturalProductCardHeight(referenceItem);
      const adCardContent = adCard.querySelector('.collection-ad-card');
      if (!referenceHeight || !adCardContent) return;

      const height = `${referenceHeight}px`;
      adCard.style.height = height;
      adCardContent.style.height = height;
      adCardContent.style.minHeight = '0';
      adCardContent.style.aspectRatio = 'auto';
    });
  };

  function scheduleAdCardHeightSync(grid) {
    if (!grid) return;
    window.cancelAnimationFrame(adCardHeightFrame);
    adCardHeightFrame = window.requestAnimationFrame(() => syncAdCardHeights(grid));
  }

  const observeAdCardGrid = (grid) => {
    if (!adCardGridResizeObserver || observedAdCardGrid === grid) return;
    adCardGridResizeObserver.disconnect();
    observedAdCardGrid = grid;
    adCardGridResizeObserver.observe(grid);
  };

  const mountAdCards = (container = document.getElementById('ProductGridContainer')) => {
    if (!container) return;

    const grid = container.querySelector('#product-grid');
    if (!grid || grid.tagName !== 'UL') return;

    grid.querySelectorAll('[data-collection-ad-card-mounted]').forEach((card) => card.remove());

    const templates = Array.from(container.querySelectorAll('template[data-collection-ad-card-template]'))
      .map((template) => ({ template, position: Number.parseInt(template.dataset.sortBy, 10) }))
      .filter(({ position }) => Number.isInteger(position) && position > 0)
      .sort((a, b) => a.position - b.position);

    templates.forEach(({ template, position }) => {
      const productCount = grid.querySelectorAll(':scope > .grid__item:not([data-collection-ad-card-mounted])').length;
      if (position > productCount + 1) return;

      const card = template.content.firstElementChild?.cloneNode(true);
      if (!card) return;

      const target = grid.children[position - 1];
      if (target) grid.insertBefore(card, target);
      else grid.appendChild(card);
    });

    observeAdCardGrid(grid);
    scheduleAdCardHeightSync(grid);
  };

  const applyQuickOptionsVariant = (dialog) => {
    const optionPickers = Array.from(dialog.querySelectorAll('[data-quick-option-index]'));
    const selectedOptions = optionPickers.map((picker) => picker.dataset.value);
    const variant = optionPickers.length
      ? dialog.quickOptionsVariants.find((item) =>
          item.options.every((option, index) => option === selectedOptions[index])
        )
      : dialog.quickOptionsVariants[0];
    const variantInput = dialog.querySelector('.product-variant-id');
    const submitButton = dialog.querySelector('.collection-quick-options__submit');
    const submitLabel = dialog.querySelector('[data-quick-options-submit-label]');
    const price = dialog.querySelector('[data-quick-options-price]');
    const image = dialog.querySelector('.collection-quick-options__image');
    const available = Boolean(variant?.available);

    variantInput.value = variant?.id || '';
    variantInput.disabled = !variant;
    submitButton.disabled = !available;
    submitLabel.textContent = variant
      ? available
        ? dialog.dataset.addLabel
        : dialog.dataset.soldLabel
      : dialog.dataset.unavailableLabel;

    if (!variant) return;
    price.textContent = variant.price;
    if (image && variant.image) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
  };

  const closeQuickOptionPickers = (dialog) => {
    dialog.querySelectorAll('[data-quick-option-index]').forEach((picker) => {
      picker.classList.remove('is-open');
      picker.querySelector('[data-quick-option-trigger]').setAttribute('aria-expanded', 'false');
      picker.querySelector('.collection-quick-options__option-list').hidden = true;
    });
  };

  const setQuickOptionValue = (picker, value) => {
    picker.dataset.value = value;
    picker.querySelector('[data-quick-selected-option]').textContent = value;
    picker.querySelectorAll('[data-quick-option-value]').forEach((option) => {
      const isSelected = option.dataset.quickOptionValue === value;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-selected', String(isSelected));
    });
  };

  const initializeQuickOptions = (dialog) => {
    if (dialog.dataset.quickOptionsInitialized === 'true') return;

    dialog.dataset.quickOptionsInitialized = 'true';
    dialog.quickOptionsVariants = JSON.parse(dialog.querySelector('[data-quick-options-variants]').textContent);
    dialog.querySelectorAll('[data-quick-option-index]').forEach((picker) => {
      const trigger = picker.querySelector('[data-quick-option-trigger]');
      const optionList = picker.querySelector('.collection-quick-options__option-list');

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const shouldOpen = !picker.classList.contains('is-open');
        closeQuickOptionPickers(dialog);
        if (!shouldOpen) return;
        picker.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        optionList.hidden = false;
      });

      picker.querySelectorAll('[data-quick-option-value]').forEach((option) => {
        option.addEventListener('click', (event) => {
          event.stopPropagation();
          setQuickOptionValue(picker, option.dataset.quickOptionValue);
          closeQuickOptionPickers(dialog);
          applyQuickOptionsVariant(dialog);
          trigger.focus();
        });
      });
    });
    dialog.addEventListener('click', (event) => {
      if (!event.target.closest('.collection-quick-options__select-wrap')) closeQuickOptionPickers(dialog);
    });
    dialog.addEventListener('keydown', (event) => {
      const picker = event.target.closest('[data-quick-option-index]');
      if (!picker) return;
      const trigger = picker.querySelector('[data-quick-option-trigger]');
      const options = Array.from(picker.querySelectorAll('[data-quick-option-value]'));

      if (event.key === 'Escape' && picker.classList.contains('is-open')) {
        event.preventDefault();
        event.stopPropagation();
        closeQuickOptionPickers(dialog);
        trigger.focus();
        return;
      }

      if (event.target === trigger && event.key === 'ArrowDown') {
        event.preventDefault();
        trigger.click();
        picker.querySelector('.collection-quick-options__option.is-selected')?.focus();
        return;
      }

      const currentIndex = options.indexOf(event.target);
      if (currentIndex < 0) return;
      let nextIndex;
      if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, options.length - 1);
      if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = options.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      options[nextIndex].focus();
    });
    dialog.addEventListener('close', () => {
      closeQuickOptionPickers(dialog);
      document.body.classList.remove('collection-quick-options-open');
    });
    applyQuickOptionsVariant(dialog);
  };

  const initializeCollectionGrid = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.dataset.collectionGridInitialized === 'true') return;

    container.dataset.collectionGridInitialized = 'true';
    initializeMobileFacets(container.closest('.shopify-section'));
    mountAdCards(container);

    new MutationObserver(() => mountAdCards(container)).observe(container, { childList: true });
  };

  const updatePriceSlider = (slider, changedRange) => {
    const priceRange = slider.closest('price-range');
    const minRange = slider.querySelector('[data-price-range="min"]');
    const maxRange = slider.querySelector('[data-price-range="max"]');
    const fields = priceRange?.querySelectorAll('.field__input');
    if (!priceRange || !minRange || !maxRange || fields.length < 2) return null;

    let minValue = Number(minRange.value);
    let maxValue = Number(maxRange.value);
    const rangeMax = Number(maxRange.max);

    if (changedRange === minRange && minValue > maxValue) minValue = maxValue;
    if (changedRange === maxRange && maxValue < minValue) maxValue = minValue;

    minRange.value = String(minValue);
    maxRange.value = String(maxValue);
    minRange.setAttribute('aria-valuemax', String(maxValue));
    maxRange.setAttribute('aria-valuemin', String(minValue));
    fields[0].value = minValue <= Number(minRange.min) ? '' : String(minValue);
    fields[1].value = maxValue >= rangeMax ? '' : String(maxValue);

    const percentage = (value) => `${rangeMax > 0 ? (value / rangeMax) * 100 : 0}%`;
    slider.style.setProperty('--price-min', percentage(minValue));
    slider.style.setProperty('--price-max', percentage(maxValue));

    return changedRange === minRange ? fields[0] : fields[1];
  };

  const syncPriceSliderFromField = (field) => {
    const priceRange = field.closest('price-range');
    const slider = priceRange?.querySelector('[data-price-slider]');
    if (!slider) return;

    const fields = Array.from(priceRange.querySelectorAll('.field__input'));
    const ranges = slider.querySelectorAll('[data-price-range]');
    const fieldIndex = fields.indexOf(field);
    if (fieldIndex < 0 || !ranges[fieldIndex]) return;

    const fallback = fieldIndex === 0 ? ranges[fieldIndex].min : ranges[fieldIndex].max;
    const rawValue = field.value.trim();
    const value = rawValue === '' ? Number(fallback) : Number(rawValue.replace(/,/g, ''));
    ranges[fieldIndex].value = Number.isFinite(value) ? String(value) : fallback;
    updatePriceSlider(slider, ranges[fieldIndex]);
  };

  const animateDesktopFacet = (summary, event) => {
    const details = summary.parentElement;
    const content = summary.nextElementSibling;
    if (!details || !content?.classList.contains('facets__display-vertical')) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    event.preventDefault();
    if (details.dataset.facetAnimating === 'true') return true;

    const opening = !details.open;
    details.dataset.facetAnimating = 'true';
    if (opening) details.open = true;

    content.style.overflow = 'hidden';
    const animation = content.animate(
      [
        { height: opening ? '0px' : `${content.offsetHeight}px`, opacity: opening ? 0 : 1 },
        { height: opening ? `${content.scrollHeight}px` : '0px', opacity: opening ? 1 : 0 },
      ],
      { duration: 250, easing: 'ease' }
    );

    animation.addEventListener('finish', () => {
      if (!opening) details.open = false;
      content.style.removeProperty('overflow');
      delete details.dataset.facetAnimating;
    }, { once: true });

    return true;
  };

  const initializeMobileFacets = (section) => {
    const drawer = section?.querySelector('[data-collection-mobile-facets]');
    if (!drawer || drawer.dataset.collectionMobileFacetsInitialized === 'true') return;

    drawer.dataset.collectionMobileFacetsInitialized = 'true';
    drawer.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-mobile-facets-close]');
      if (closeButton) {
        event.preventDefault();
        event.stopPropagation();
        drawer.querySelector(':scope > details > summary')?.click();
        return;
      }

    }, true);

    const productCount = section.querySelector('#ProductCount');
    const resultButton = drawer.querySelector('[data-mobile-filter-results-button]');
    if (!productCount || !resultButton) return;

    const updateResultButton = () => {
      const count = productCount.textContent.replace(/,/g, '').match(/\d+/)?.[0];
      if (count) resultButton.textContent = `${resultButton.dataset.resultsLabel} (${count})`;
    };

    updateResultButton();
    new MutationObserver(updateResultButton).observe(productCount, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  };

  document.addEventListener('input', (event) => {
    const range = event.target.closest?.('[data-price-range]');
    if (!range) return;

    event.stopPropagation();
    updatePriceSlider(range.closest('[data-price-slider]'), range);
  }, true);

  document.addEventListener('change', (event) => {
    const range = event.target.closest?.('[data-price-range]');
    if (!range) return;

    const field = updatePriceSlider(range.closest('[data-price-slider]'), range);
    field?.dispatchEvent(new Event('input', { bubbles: true }));
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches?.('#SortBy-mobile-bar')) {
      const section = event.target.closest('.shopify-section');
      const mobileSortValue = section?.querySelector('[data-compact-mobile-sort-value]');
      if (mobileSortValue) mobileSortValue.value = event.target.value;
    }

    if (event.target.matches?.('price-range .field__input')) syncPriceSliderFromField(event.target);
  });

  document.addEventListener('click', (event) => {
    const facetSummary = event.target.closest('.facets__disclosure-vertical > .facets__summary');
    if (facetSummary && animateDesktopFacet(facetSummary, event)) return;

    const toggle = event.target.closest('[data-collection-filter-toggle]');
    if (toggle) {
      const layout = toggle.closest('.shopify-section')?.querySelector('[data-collection-grid-layout]')
        || document.querySelector('[data-collection-grid-layout]');
      if (!layout) return;

      const isHidden = layout.classList.toggle('is-filter-hidden');
      toggle.setAttribute('aria-expanded', String(!isHidden));
      const label = toggle.querySelector('[data-collection-filter-toggle-label]');
      if (label) label.textContent = isHidden ? toggle.dataset.showLabel : toggle.dataset.hideLabel;
      return;
    }

    const quickOptionsOpen = event.target.closest('[data-quick-options-open]');
    if (quickOptionsOpen) {
      const dialog = document.getElementById(quickOptionsOpen.getAttribute('aria-controls'));
      if (!dialog) return;
      initializeQuickOptions(dialog);
      dialog.showModal();
      document.body.classList.add('collection-quick-options-open');
      return;
    }

    const quickOptionsClose = event.target.closest('[data-quick-options-close]');
    if (quickOptionsClose) {
      quickOptionsClose.closest('[data-quick-options-dialog]')?.close();
      return;
    }

    const dialog = event.target.closest('[data-quick-options-dialog]');
    if (dialog && event.target === dialog) {
      const rect = dialog.getBoundingClientRect();
      const isOutside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (isOutside) dialog.close();
      return;
    }

    const swatch = event.target.closest('.new-product-card-2__swatch');
    if (!swatch) return;

    const card = swatch.closest('.new-product-card-2');
    const image = card?.querySelector('.new-product-card-2__image');
    const swatchImage = swatch.querySelector('img');
    const coverSrc = swatchImage?.src?.replace(/([?&])width=\d+/, '$1width=600') || swatch.dataset.coverSrc;
    if (!card || !image || !coverSrc) return;

    image.removeAttribute('srcset');
    image.src = coverSrc;
    image.alt = swatch.dataset.coverAlt || card.dataset.productTitle || '';
    card.querySelectorAll('.new-product-card-2__swatch').forEach((item) => {
      const isActive = item === swatch;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-pressed', String(isActive));
    });
  });

  window.mountCollectionAdCards = mountAdCards;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form') return;
      const dialog = document.querySelector('[data-quick-options-dialog][open]');
      const variantInput = dialog?.querySelector('.product-variant-id');
      if (dialog && String(variantInput?.value) === String(event.productVariantId)) dialog.close();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeCollectionGrid);
  else initializeCollectionGrid();
  document.addEventListener('shopify:section:load', initializeCollectionGrid);
})();
